import React, { useState, useEffect } from "react";
import PropTypes from 'prop-types';
import SubscriptionModal from "./SubscriptionModal";
import { createSubscriptionPayment, verifyPaymentStatus } from "../services/paymentService";
import { getUserProfile, firestore, auth } from "../config/firebase";
import firebase from "firebase/app";
import "firebase/auth";

const SubscriptionPage = ({ profile: initialProfile, user: userProp }) => {
    const [selectedPlan, setSelectedPlan] = useState("annual");
    const [isLoading, setIsLoading] = useState(false);
    const [userProfile, setUserProfile] = useState(null);
    const [error, setError] = useState(null);
    const [currentUser, setCurrentUser] = useState(null);
    const [modalConfig, setModalConfig] = useState({
        show: false,
        subscriptionStatus: {},
        payLink: null,
        isLoading: false,
        message: null
    });

    const BASE_PRICE = 175000;
    const PENDING_PAYMENT_EXPIRY = 3600000;

    useEffect(() => {
        const initializeUser = async () => {
            try {
                const unsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {
                    if (firebaseUser) {
                        setCurrentUser(firebaseUser);
                        try {
                            const profile = await getUserProfile(firebaseUser.uid);
                            setUserProfile(profile);
                            setError(null);
                        } catch (err) {
                            setError("Impossible de charger votre profil. Veuillez réessayer.");
                        }
                    } else {
                        setError("Vous devez être connecté pour accéder aux abonnements.");
                    }
                });

                return () => unsubscribe();
            } catch (err) {
                setError("Erreur de configuration. Veuillez réessayer ultérieurement.");
            }
        };

        initializeUser();
    }, []);

    const profile = {
        email: userProfile?.email || currentUser?.email || "user@example.com",
        name: userProfile?.name || currentUser?.displayName || "Utilisateur",
        subscription: {
            status: userProfile?.subscription?.status || "inactif",
            plan: userProfile?.subscription?.planName || "",
            startDate: userProfile?.subscription?.startDate
                ? userProfile.subscription.startDate.toDate()
                : null,
            endDate: userProfile?.subscription?.endDate
                ? userProfile.subscription.endDate.toDate()
                : null,
        },
    };

    const PLANS = {
        annual: {
            id: "annual",
            name: "Abonnement Annuel",
            amount: BASE_PRICE,
            period: "an",
            discount: "2 mois gratuits",
            popular: true,
            benefits: [
                "Accès complet à toutes les fonctionnalités",
                "Support prioritaire 24/7",
                "Mises à jour automatiques incluses",
                "Synchronisation multi-appareils"
            ],
            cta: "Choisir l'abonnement annuel"
        },
        quarterly: {
            id: "quarterly",
            name: "Abonnement Trimestriel",
            amount: Math.round(BASE_PRICE / 4),
            period: "3 mois",
            discount: "Économisez 15%",
            popular: false,
            benefits: [
                "Accès complet à toutes les fonctionnalités",
                "Support prioritaire 24/7",
                "Mises à jour automatiques incluses"
            ],
            cta: "Choisir le trimestriel"
        },
        monthly: {
            id: "monthly",
            name: "Abonnement Mensuel",
            amount: Math.round(BASE_PRICE / 10),
            period: "mois",
            discount: "Flexibilité mensuelle",
            popular: false,
            benefits: [
                "Accès complet à toutes les fonctionnalités",
                "Support prioritaire 24/7",
                "Mises à jour incluses"
            ],
            cta: "Choisir le mensuel"
        },
    };

    useEffect(() => {
        const checkPendingPayment = async () => {
            if (!currentUser?.uid) return;

            const pendingData = localStorage.getItem('pendingSubscription');
            if (!pendingData) return;

            const { planId, timestamp } = JSON.parse(pendingData);

            if (Date.now() - timestamp > PENDING_PAYMENT_EXPIRY) {
                localStorage.removeItem('pendingSubscription');
                return;
            }

            try {
                const paymentId = new URLSearchParams(window.location.search).get('payment_id');
                if (paymentId) {
                    const { status } = await verifyPaymentStatus(paymentId);
                    if (status === 'completed') {
                        handlePaymentSuccess(planId);
                    }
                }
            } catch (error) {
                setModalConfig(prev => ({
                    ...prev,
                    message: `Erreur de vérification: ${error.message}`
                }));
            }
        };

        checkPendingPayment();
    }, [currentUser]);

    const handlePaymentSuccess = async (planId) => {
        localStorage.removeItem('pendingSubscription');
        window.history.replaceState({}, document.title, window.location.pathname);

        if (!currentUser?.uid) {
            setModalConfig({
                show: true,
                subscriptionStatus: { status: "error" },
                isLoading: false,
                payLink: null,
                message: "Erreur: Utilisateur non identifié. Veuillez vous reconnecter."
            });
            return;
        }

        try {
            const today = firebase.firestore.Timestamp.now();
            const expiryDate = firebase.firestore.Timestamp.fromDate(new Date());

            if (planId === "annual") {
                expiryDate.seconds += 365 * 24 * 3600;
            } else if (planId === "quarterly") {
                expiryDate.seconds += 90 * 24 * 3600;
            } else if (planId === "monthly") {
                expiryDate.seconds += 30 * 24 * 3600;
            }

            const userRef = firestore.collection('users').doc(currentUser.uid);
            await userRef.update({
                "subscription.status": "actif",
                "subscription.planName": planId,
                "subscription.startDate": today,
                "subscription.endDate": expiryDate,
                "updatedAt": firebase.firestore.FieldValue.serverTimestamp(),
            });

            const updatedProfile = await getUserProfile(currentUser.uid);
            setUserProfile(updatedProfile);

            setModalConfig({
                show: true,
                subscriptionStatus: {
                    status: "success",
                    plan: planId
                },
                isLoading: false,
                payLink: null,
                message: "Paiement confirmé ! Votre abonnement est maintenant actif."
            });

        } catch (error) {
            setModalConfig({
                show: true,
                subscriptionStatus: { status: "warning" },
                isLoading: false,
                payLink: null,
                message: `Erreur lors de l'activation: ${error.message}.`
            });
        }
    };

    const formatPrice = (amount) => {
        return new Intl.NumberFormat("fr-FR", {
            style: "currency",
            currency: "XOF",
            minimumFractionDigits: 0,
        }).format(amount);
    };

    const handleSubscribe = async (planId) => {
        if (!currentUser?.uid) {
            alert("Vous devez être connecté pour souscrire à un abonnement.");
            return;
        }

        const plan = PLANS[planId];
        initializeModal(plan);

        try {
            const paymentData = await processPayment(plan);
            updateModalWithPaymentLink(paymentData, planId);
        } catch (error) {
            handlePaymentError(error);
        } finally {
            setIsLoading(false);
        }
    };

    const hasActiveSubscription = () => {
        return profile.subscription.active &&
            profile.subscription.expiresAt &&
            new Date(profile.subscription.expiresAt) > new Date();
    };

    const initializeModal = (plan) => {
        setModalConfig({
            show: true,
            subscriptionStatus: {
                status: "new",
                nomPlan: plan.name,
                montant: plan.amount,
                periode: plan.period,
                avantages: plan.benefits
            },
            isLoading: true,
            payLink: null,
            message: null
        });
        setIsLoading(true);
    };

    const processPayment = async (plan) => {
        if (!currentUser?.uid) {
            throw new Error("Utilisateur non authentifié. Veuillez vous reconnecter.");
        }

        if (!plan?.amount || plan.amount <= 0) {
            throw new Error("Plan d'abonnement invalide.");
        }

        const fee = Math.max(Math.round(plan.amount * 0.01));
        const totalAmount = plan.amount + fee;

        const userData = {
            uid: currentUser.uid,
            email: currentUser.email || profile.email,
            name: userProfile?.name || currentUser.displayName || "Utilisateur",
            displayName: currentUser.displayName,
            photoURL: currentUser.photoURL,
            userProfile: userProfile || {}
        };

        const planData = {
            id: plan.id,
            name: plan.name,
            amount: totalAmount,
            period: plan.period,
            benefits: plan.benefits,
            originalAmount: plan.amount,
            fee: fee,
            description: `Abonnement ${plan.name}`
        };

        const paymentLink = await createSubscriptionPayment(userData, planData);

        if (!paymentLink) {
            throw new Error("Impossible de générer le lien de paiement");
        }

        return { paymentUrl: paymentLink };
    };

    const updateModalWithPaymentLink = (paymentData, planId) => {
        setModalConfig(prev => ({
            ...prev,
            payLink: paymentData.paymentUrl,
            isLoading: false
        }));

        localStorage.setItem('pendingSubscription', JSON.stringify({
            planId,
            timestamp: Date.now()
        }));
    };

    const handlePaymentError = (error) => {
        setModalConfig(prev => ({
            ...prev,
            isLoading: false,
            message: `Erreur: ${error.message}`
        }));
    };

    const styles = {
        container: {
            fontFamily: "system-ui, -apple-system, sans-serif",
            backgroundColor: "#f8fafc",
            minHeight: "100vh",
            padding: "2rem"
        },
        header: {
            textAlign: "center",
            marginBottom: "4rem"
        },
        title: {
            fontSize: "3rem",
            fontWeight: 700,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            marginBottom: "1rem"
        },
        subtitle: {
            color: "#64748b",
            fontSize: "1.25rem",
            maxWidth: "600px",
            margin: "0 auto",
            lineHeight: 1.6
        },
        plansContainer: {
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
            gap: "2rem",
            maxWidth: "1000px",
            margin: "0 auto"
        },
        planCard: (isSelected) => ({
            background: "#ffffff",
            borderRadius: "20px",
            padding: "2.5rem",
            boxShadow: isSelected
                ? "0 0 0 3px #667eea, 0 10px 30px rgba(0,0,0,0.1)"
                : "0 8px 25px rgba(0,0,0,0.08)",
            position: "relative",
            cursor: "pointer",
            border: "1px solid #e2e8f0",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }),
        popularBadge: {
            position: "absolute",
            top: "-12px",
            left: "50%",
            transform: "translateX(-50%)",
            color: "white",
            padding: "0.5rem 1.5rem",
            borderRadius: "20px",
            fontSize: "0.875rem",
            fontWeight: "600",
            boxShadow: "0 4px 12px rgba(102, 126, 234, 0.4)",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            animation: "pulse 2s infinite"
        },
        activeSubscription: {
            maxWidth: "600px",
            margin: "3rem auto 0",
            padding: "1.5rem",
            background: "#f0fdf4",
            border: "1px solid #bbf7d0",
            borderRadius: "12px",
            textAlign: "center"
        }
    };

    if (!currentUser) {
        return (
            <div style={{...styles.container, display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                <div style={{textAlign: 'center'}}>
                    <div style={{
                        width: "50px",
                        height: "50px",
                        border: "4px solid #f3f4f6",
                        borderTopColor: "#667eea",
                        borderRadius: "50%",
                        animation: "spin 1s linear infinite",
                        margin: "0 auto 1rem"
                    }} />
                    <p style={{color: "#64748b"}}>Chargement...</p>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <style>{`
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
                @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.8; }
                }
            `}</style>

            <header style={styles.header}>
                <h1 style={styles.title}>Choisissez votre plan</h1>
                <p style={styles.subtitle}>
                    Accédez à toutes les fonctionnalités avec nos plans flexibles
                </p>
            </header>

            {error && (
                <div style={{
                    background: "#fee2e2",
                    color: "#b91c1c",
                    padding: "1rem",
                    borderRadius: "8px",
                    marginBottom: "2rem",
                    textAlign: "center",
                    maxWidth: "800px",
                    margin: "0 auto 2rem"
                }}>
                    {error}
                </div>
            )}

            <div style={styles.plansContainer}>
                {Object.values(PLANS).map((plan) => {
                    const isSelected = selectedPlan === plan.id;
                    const isCurrentlyLoading = isLoading && isSelected;

                    return (
                        <div
                            key={plan.id}
                            style={styles.planCard(isSelected)}
                            onClick={() => !isLoading && setSelectedPlan(plan.id)}
                        >
                            {plan.popular && (
                                <div style={styles.popularBadge}>
                                    ⭐ Le plus populaire
                                </div>
                            )}

                            <div style={{ textAlign: "center", marginBottom: "2rem" }}>
                                <h2 style={{
                                    fontSize: "1.5rem",
                                    fontWeight: 600,
                                    marginBottom: "0.5rem",
                                    color: "#1e293b"
                                }}>
                                    {plan.name}
                                </h2>
                                <p style={{
                                    color: "#10b981",
                                    fontSize: "0.875rem",
                                    fontWeight: "500"
                                }}>
                                    💡 {plan.discount}
                                </p>
                            </div>

                            <div style={{
                                textAlign: "center",
                                marginBottom: "2rem",
                                padding: "1rem 0"
                            }}>
                                <div style={{
                                    fontSize: "3rem",
                                    fontWeight: 700,
                                    color: "#667eea",
                                    lineHeight: 1
                                }}>
                                    {formatPrice(plan.amount)}
                                </div>
                                <span style={{
                                    fontSize: "1rem",
                                    color: "#64748b",
                                    fontWeight: "500"
                                }}>
                                    par {plan.period}
                                </span>
                            </div>

                            <ul style={{
                                margin: "0 0 2rem 0",
                                padding: "0",
                                listStyle: "none"
                            }}>
                                {plan.benefits.map((benefit, i) => (
                                    <li key={i} style={{
                                        marginBottom: "0.75rem",
                                        color: "#374151",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "0.75rem"
                                    }}>
                                        <span style={{
                                            color: "#10b981",
                                            fontSize: "1.2rem"
                                        }}>
                                            ✓
                                        </span>
                                        {benefit}
                                    </li>
                                ))}
                            </ul>

                            <button
                                style={{
                                    width: "100%",
                                    padding: "1rem",
                                    background: isSelected
                                        ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                                        : "#f1f5f9",
                                    color: isSelected ? "white" : "#64748b",
                                    border: "none",
                                    borderRadius: "12px",
                                    fontWeight: "600",
                                    fontSize: "1rem",
                                    cursor: isCurrentlyLoading ? "not-allowed" : "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "0.5rem",
                                    transition: "all 0.3s ease",
                                    opacity: isCurrentlyLoading ? 0.7 : 1
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (!isLoading) {
                                        handleSubscribe(plan.id);
                                    }
                                }}
                                disabled={isLoading}
                            >
                                {isCurrentlyLoading ? (
                                    <>
                                        <div style={{
                                            width: "20px",
                                            height: "20px",
                                            border: "3px solid rgba(255,255,255,0.3)",
                                            borderTopColor: "#fff",
                                            borderRadius: "50%",
                                            animation: "spin 1s linear infinite"
                                        }} />
                                        Traitement...
                                    </>
                                ) : (
                                    plan.cta
                                )}
                            </button>
                        </div>
                    );
                })}
            </div>

            {hasActiveSubscription() && (
                <div style={styles.activeSubscription}>
                    <h3 style={{ color: "#166534", marginBottom: "0.5rem" }}>
                        ✅ Abonnement actif
                    </h3>
                    <p style={{ color: "#15803d", margin: 0 }}>
                        Plan: {profile.subscription.plan} •
                        Expire le: {profile.subscription.expiresAt?.toLocaleDateString('fr-FR')}
                    </p>
                </div>
            )}

            <SubscriptionModal
                show={modalConfig.show}
                onClose={() => setModalConfig(prev => ({...prev, show: false}))}
                subscriptionStatus={modalConfig.subscriptionStatus}
                payLink={modalConfig.payLink}
                isLoading={modalConfig.isLoading}
                message={modalConfig.message}
            />
        </div>
    );
};

SubscriptionPage.propTypes = {
    profile: PropTypes.object,
    user: PropTypes.object
};

export default SubscriptionPage;